/**
 * queueStore — in-memory mirror of the autonomous ticket queue's
 * filesystem state, refreshed via the existing file-tree-event stream
 * the Rust file watcher already emits. Each folder is a separate list
 * so the kanban can re-render only what changed.
 *
 * The store is project-scoped — switching the active project resets it
 * and re-fetches everything from scratch.
 */
import { create } from 'zustand';
import { useEffect } from 'react';
import { isLocalShell } from '@/lib/platform';
import { getBackend } from '@/services/backend';
import { logger } from '@/lib/logger';
import { showToast } from '@/components/ui/Toast';
import { getWorkQueueConfig, type AteliercodeConfig } from '@/lib/projectSettings';

const TAG = 'queueStore';

export type QueueFolder = 'todo' | 'in-progress' | 'review' | 'fix' | 'done' | 'escalate';
export const QUEUE_FOLDERS: QueueFolder[] = ['todo', 'in-progress', 'review', 'fix', 'done', 'escalate'];

export interface TicketSummary {
  filename: string;
  folder: QueueFolder;
  title: string;
  attempt_count: number;
  modified_ms: number;
  /** Who executes this ticket: local (Qwen, default) | claude. */
  engine?: 'local' | 'claude';
  /** The epic id this ticket belongs to (from frontmatter). */
  epic?: string;
}

export interface EpicSummary {
  id: string;
  title: string;
  status: string;
  idea: string | null;
  ticket_count: number;
  filename: string;
}

export interface IdeaSummary {
  filename: string;
  preview: string;
  converted: boolean;
  linked_ticket: string | null;
  modified_ms: number;
}

/** Folders whose ready `engine:claude` tickets the auto-loop won't run —
 *  they're the user's responsibility to execute in an interactive session.
 *  in-progress/review/done/escalate are mid-flight or terminal, so excluded. */
const CLAUDE_WAIT_FOLDERS: QueueFolder[] = ['todo', 'fix'];

/** Folders that hold *ready* (not-yet-claimed) work the auto-loop can pull. */
const READY_FOLDERS: QueueFolder[] = ['todo', 'fix'];

/** A single move on the board, for the heartbeat event feed. Derived by diffing
 *  successive byFolder snapshots — each ticket that changed folder (or first
 *  appeared in-progress) becomes one entry. */
export interface QueueEvent {
  /** Monotonic id so React keys stay stable as old events scroll off. */
  id: number;
  /** Short ticket id (the leading NNN of the filename). */
  ticketId: string;
  filename: string;
  /** Human label, e.g. "claimed", "→ review", "→ done". */
  label: string;
  atMs: number;
}

/** Max events kept in the scrolling feed. */
const MAX_EVENTS = 40;

interface QueueState {
  /** projectId currently mirrored. null = nothing loaded. */
  projectId: string | null;
  byFolder: Record<QueueFolder, TicketSummary[]>;
  inbox: IdeaSummary[];
  epics: EpicSummary[];
  /** Ready `engine:claude` tickets in todo/+fix/ the auto-loop skips by the
   *  cost rule (claude runs in the user's interactive session). Derived on
   *  every refresh from byFolder. */
  claudeWaiting: TicketSummary[];
  /** True when the loop has drained all *local* work (none in-progress, none
   *  ready) yet there ARE ready `engine:claude` tickets — i.e. the queue is
   *  gated on the user running Claude tickets. Derived from byFolder. */
  blocking: boolean;
  /** True when the project's work.roles.worker maps to a Claude agent. In that
   *  setup Claude IS the queue's runner, so "tickets are waiting on Claude" is
   *  the normal state, not a gate — the banner and toast are suppressed. Read
   *  from .ateliercode/config.json on every refresh. */
  workerIsClaude: boolean;
  /** Claude-waiting count at the moment the user dismissed the banner; the
   *  banner stays hidden while the count hasn't grown past this. 0 = not
   *  dismissed. Reset on project switch. */
  claudeBannerDismissedCount: number;
  /** When the store first saw each currently in-progress ticket, keyed by
   *  filename → epoch ms. Powers the per-ticket elapsed timer. Reset per
   *  project; entries drop when a ticket leaves in-progress. */
  inProgressSince: Record<string, number>;
  /** Recent board moves (newest first) for the heartbeat feed. */
  events: QueueEvent[];
  loading: boolean;
  error: string | null;

  setProject: (projectId: string | null) => void;
  refresh: () => Promise<void>;
  refreshFolder: (folder: QueueFolder) => Promise<void>;
  refreshInbox: () => Promise<void>;
  refreshEpics: () => Promise<void>;
  setEngine: (folder: QueueFolder, filename: string, engine: 'local' | 'claude') => Promise<void>;
  /** Hide the claude-waiting banner until more claude tickets arrive. */
  dismissClaudeBanner: () => void;
}

/** Recompute the claude-waiting list from byFolder and toast on a 0→>0
 *  rising edge (calm: only when the count actually crosses from none to some,
 *  not on every refresh). */
function deriveClaudeWaiting(byFolder: Record<QueueFolder, TicketSummary[]>): TicketSummary[] {
  const waiting: TicketSummary[] = [];
  for (const folder of CLAUDE_WAIT_FOLDERS) {
    for (const t of byFolder[folder] ?? []) {
      if (t.engine === 'claude') waiting.push(t);
    }
  }
  return waiting;
}

/** Derive the "blocking" gate: the loop has nothing local left to do AND there
 *  is ready claude-engine work waiting. Specifically: NO local ticket is
 *  in-progress, NO local ticket is ready (todo/fix), but at least one ready
 *  `engine:claude` ticket exists. That means the autonomous loop has drained
 *  every task it can run itself and progress now depends on the user executing
 *  the Claude tickets in their session. */
function deriveBlocking(byFolder: Record<QueueFolder, TicketSummary[]>): boolean {
  const isLocal = (t: TicketSummary) => (t.engine ?? 'local') === 'local';
  const localInProgress = (byFolder['in-progress'] ?? []).some(isLocal);
  if (localInProgress) return false;
  const localReady = READY_FOLDERS.some((f) => (byFolder[f] ?? []).some(isLocal));
  if (localReady) return false;
  const claudeReady = READY_FOLDERS.some((f) =>
    (byFolder[f] ?? []).some((t) => t.engine === 'claude'),
  );
  return claudeReady;
}

/** Recompute the in-progress "first seen" map: keep existing timestamps for
 *  tickets still in-progress, stamp newly-arrived ones with `now`, and drop
 *  any that have left. Pure — returns a fresh object so the timer stays stable
 *  for a ticket across refreshes (no flicker). */
function deriveInProgressSince(
  prev: Record<string, number>,
  inProgress: TicketSummary[],
  now: number,
): Record<string, number> {
  const next: Record<string, number> = {};
  for (const t of inProgress) {
    next[t.filename] = prev[t.filename] ?? now;
  }
  return next;
}

/** Diff two folder snapshots into feed events: any ticket whose folder changed
 *  (or that newly appeared) is one move. Returns newest-first, capped. */
let eventSeq = 0;
function deriveEvents(
  prevEvents: QueueEvent[],
  prevByFolder: Record<QueueFolder, TicketSummary[]>,
  nextByFolder: Record<QueueFolder, TicketSummary[]>,
  now: number,
): QueueEvent[] {
  const prevLoc = new Map<string, QueueFolder>();
  for (const f of QUEUE_FOLDERS) for (const t of prevByFolder[f] ?? []) prevLoc.set(t.filename, f);
  const fresh: QueueEvent[] = [];
  for (const f of QUEUE_FOLDERS) {
    for (const t of nextByFolder[f] ?? []) {
      const was = prevLoc.get(t.filename);
      if (was === f) continue; // unchanged
      const label =
        f === 'in-progress' ? 'claimed'
        : f === 'done' ? '→ done'
        : `→ ${f}`;
      fresh.push({
        id: ++eventSeq,
        ticketId: t.filename.split('-')[0],
        filename: t.filename,
        label,
        atMs: now,
      });
    }
  }
  if (fresh.length === 0) return prevEvents;
  return [...fresh.reverse(), ...prevEvents].slice(0, MAX_EVENTS);
}

// Previous claude-waiting count per project, so we only toast on the rising
// edge (0 → >0) and reset cleanly when switching projects.
const prevClaudeWaitingByProject: Record<string, number> = {};

function maybeToastClaudeWaiting(projectId: string, count: number): void {
  const prev = prevClaudeWaitingByProject[projectId] ?? 0;
  prevClaudeWaitingByProject[projectId] = count;
  if (prev === 0 && count > 0) {
    showToast(
      `${count} ticket${count === 1 ? '' : 's'} need Claude — run them in your session`,
      'warning',
    );
  }
}

const EMPTY: Record<QueueFolder, TicketSummary[]> = {
  todo: [], 'in-progress': [], review: [], fix: [], done: [], escalate: [],
};

async function fetchFolder(projectId: string, folder: QueueFolder): Promise<TicketSummary[]> {
  // Backend-agnostic (ticket 065): Tauri delegates to `queue_list`, CEF routes
  // to the `queue.list` core IPC command. Cast bridges the store's folder-typed
  // summaries with the backend's string-folder shape (same wire fields).
  return (await getBackend().queue.list(projectId, folder)) as TicketSummary[];
}

async function fetchInbox(projectId: string): Promise<IdeaSummary[]> {
  return (await getBackend().queue.inboxList(projectId)) as IdeaSummary[];
}

async function fetchEpics(projectId: string): Promise<EpicSummary[]> {
  return (await getBackend().queue.listEpics(projectId)) as EpicSummary[];
}

/** True when the project's configured queue *worker* is a Claude agent — then
 *  claude-engine tickets are the loop's normal diet, not user-gated work.
 *  Reads .ateliercode/config.json; any read/parse failure means "not claude"
 *  (the conservative default keeps the banner behavior for unconfigured
 *  projects). */
async function fetchWorkerIsClaude(projectId: string): Promise<boolean> {
  try {
    const raw = await getBackend().files.readAteliercodeConfig(projectId);
    if (!raw) return false;
    const cfg = JSON.parse(raw) as AteliercodeConfig;
    const work = getWorkQueueConfig(cfg);
    const worker = work.agents[work.roles.worker];
    return !!worker && /claude/i.test(`${work.roles.worker} ${worker.command}`);
  } catch {
    return false;
  }
}

export const useQueueStore = create<QueueState>((set, get) => ({
  projectId: null,
  byFolder: { ...EMPTY },
  inbox: [],
  epics: [],
  claudeWaiting: [],
  blocking: false,
  workerIsClaude: false,
  claudeBannerDismissedCount: 0,
  inProgressSince: {},
  events: [],
  loading: false,
  error: null,

  setProject: (projectId) => {
    if (projectId === get().projectId) return;
    // Reset the rising-edge baseline for the project we're leaving so a later
    // return re-evaluates from scratch (avoids a stale "already toasted" state).
    const leaving = get().projectId;
    if (leaving) delete prevClaudeWaitingByProject[leaving];
    set({
      projectId,
      byFolder: { ...EMPTY },
      inbox: [],
      epics: [],
      claudeWaiting: [],
      blocking: false,
      workerIsClaude: false,
      claudeBannerDismissedCount: 0,
      inProgressSince: {},
      events: [],
      error: null,
    });
    if (projectId) {
      void get().refresh();
    }
  },

  refresh: async () => {
    const id = get().projectId;
    if (!id || !isLocalShell()) return;
    set({ loading: true, error: null });
    try {
      const folderResults = await Promise.all(
        QUEUE_FOLDERS.map(async (f) => [f, await fetchFolder(id, f)] as const),
      );
      const inbox = await fetchInbox(id);
      const epics = await fetchEpics(id);
      const workerIsClaude = await fetchWorkerIsClaude(id);
      const byFolder = { ...EMPTY };
      for (const [f, tickets] of folderResults) byFolder[f] = tickets;

      // Auto-archive lifecycle: any inbox idea linked to a ticket that's
      // now in done/ should move to inbox/archive/. Fire-and-forget; if
      // the archive call fails the idea stays visible (greyed out) until
      // the next refresh tries again — no harm.
      const doneFilenames = new Set(byFolder.done.map((t) => t.filename));
      const toArchive = inbox.filter((i) => i.linked_ticket && doneFilenames.has(i.linked_ticket));
      for (const idea of toArchive) {
        getBackend().queue.inboxArchive(id, idea.filename)
          .catch((e) => logger.debug(TAG, `archive ${idea.filename} failed`, e));
      }
      const visibleInbox = inbox.filter((i) => !toArchive.includes(i));

      const claudeWaiting = deriveClaudeWaiting(byFolder);
      if (!workerIsClaude) maybeToastClaudeWaiting(id, claudeWaiting.length);
      const now = Date.now();
      set((s) => ({
        byFolder,
        inbox: visibleInbox,
        epics,
        claudeWaiting,
        blocking: deriveBlocking(byFolder),
        workerIsClaude,
        inProgressSince: deriveInProgressSince(s.inProgressSince, byFolder['in-progress'], now),
        events: deriveEvents(s.events, s.byFolder, byFolder, now),
        loading: false,
      }));
    } catch (e) {
      logger.warn(TAG, 'refresh failed', e);
      set({ loading: false, error: e instanceof Error ? e.message : String(e) });
    }
  },

  refreshFolder: async (folder) => {
    const id = get().projectId;
    if (!id || !isLocalShell()) return;
    try {
      const tickets = await fetchFolder(id, folder);
      set((s) => {
        const byFolder = { ...s.byFolder, [folder]: tickets };
        const claudeWaiting = deriveClaudeWaiting(byFolder);
        if (CLAUDE_WAIT_FOLDERS.includes(folder) && !s.workerIsClaude) {
          maybeToastClaudeWaiting(id, claudeWaiting.length);
        }
        const now = Date.now();
        return {
          byFolder,
          claudeWaiting,
          blocking: deriveBlocking(byFolder),
          inProgressSince: deriveInProgressSince(s.inProgressSince, byFolder['in-progress'], now),
          events: deriveEvents(s.events, s.byFolder, byFolder, now),
        };
      });
    } catch (e) {
      logger.warn(TAG, `refreshFolder(${folder}) failed`, e);
    }
  },

  refreshInbox: async () => {
    const id = get().projectId;
    if (!id || !isLocalShell()) return;
    try {
      const inbox = await fetchInbox(id);
      set({ inbox });
    } catch (e) {
      logger.warn(TAG, 'refreshInbox failed', e);
    }
  },

  refreshEpics: async () => {
    const id = get().projectId;
    if (!id || !isLocalShell()) return;
    try {
      const epics = await fetchEpics(id);
      set({ epics });
    } catch (e) {
      logger.warn(TAG, 'refreshEpics failed', e);
    }
  },

  setEngine: async (folder, filename, engine) => {
    const id = get().projectId;
    if (!id || !isLocalShell()) return;
    try {
      await getBackend().queue.setEngine(id, folder, filename, engine);
      // Re-fetch the affected folder so the new engine badge shows immediately.
      await get().refreshFolder(folder);
    } catch (e) {
      logger.warn(TAG, `setEngine(${folder}/${filename}) failed`, e);
    }
  },

  dismissClaudeBanner: () => {
    set((s) => ({ claudeBannerDismissedCount: s.claudeWaiting.length }));
  },
}));

/**
 * useQueueWatcher — start a file-tree-event listener that re-fetches
 * the queue when anything under .ateliercode/work/ changes. Call from
 * QueueTab on mount; teardown is automatic.
 */
export function useQueueWatcher(projectId: string | undefined) {
  const setProject = useQueueStore((s) => s.setProject);

  // Bind project + initial load.
  useEffect(() => {
    setProject(projectId ?? null);
  }, [projectId, setProject]);

  // NOTE: live file-watch auto-refresh rode the Tauri `file-tree-event` channel,
  // which isn't routed through the core yet — so it was Tauri-only and is gone
  // under the CEF host. The kanban still works via manual refresh + the explicit
  // post-action refreshes QueueTab fires; live auto-refresh awaits a
  // `file.subscribe` core handler (a separate ticket).
}
